import { useMemo, useState } from 'react';
import {
  FEASIBILITY_LABELS,
  FEASIBILITY_QUESTIONS,
  FEASIBILITY_TYPES,
  FEASIBILITY_VIABILITY_THRESHOLD,
  buildFeasibilityConclusionReport,
  scoreFromAnswers,
  feasibilityViabilityLabel,
  viabilityFromScore,
  type EvaluationConclusionReport,
  type FeasibilityType,
} from '@isbd/shared';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/features/auth/AuthProvider';
import { buildConclusionReportPdfHtml, openPrintablePdf } from '@/lib/exportPdf';
import { useToast } from '@/features/ui/toast/useToast';

interface Props {
  ideationId: string;
  meta?: {
    title?: string;
    domain?: string;
    zone?: string;
  };
  onDone?: (finalScore: number) => void;
}

export function FeasibilityWizard({ ideationId, meta, onDone }: Props) {
  const { profile } = useAuth();
  const toast = useToast();
  const [active, setActive] = useState<FeasibilityType>('technical');
  const [answers, setAnswers] = useState<Record<FeasibilityType, Record<string, number>>>(() =>
    Object.fromEntries(FEASIBILITY_TYPES.map((t) => [t, {}])) as Record<
      FeasibilityType,
      Record<string, number>
    >,
  );
  const [savedScores, setSavedScores] = useState<Partial<Record<FeasibilityType, number>>>({});
  const [report, setReport] = useState<EvaluationConclusionReport | null>(null);
  const [loading, setLoading] = useState(false);

  const scores = useMemo(() => {
    const out = {} as Record<FeasibilityType, number>;
    for (const t of FEASIBILITY_TYPES) out[t] = scoreFromAnswers(t, answers[t] ?? {});
    return out;
  }, [answers]);

  const sum = useMemo(() => Object.values(scores).reduce((a, b) => a + b, 0), [scores]);

  async function saveCurrentStudy(): Promise<boolean> {
    if (!profile) return false;
    setLoading(true);
    try {
      const score = scores[active];
      const { error: err } = await supabase.from('feasibility_studies').upsert(
        {
          user_id: profile.id,
          ideation_id: ideationId,
          study_type: active,
          score,
          answers: answers[active],
        },
        { onConflict: 'ideation_id,study_type' },
      );
      if (err) throw err;
      setSavedScores({ ...savedScores, [active]: score });
      toast.success(`Étude ${FEASIBILITY_LABELS[active]} validée (${score} pts).`, 'Faisabilité');
      return true;
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Enregistrement impossible');
      return false;
    } finally {
      setLoading(false);
    }
  }

  async function calculateFinal() {
    if (!profile || !FEASIBILITY_TYPES.every((t) => savedScores[t] != null)) return;
    setLoading(true);
    try {
      for (const t of FEASIBILITY_TYPES) {
        const { error: err } = await supabase.from('feasibility_studies').upsert(
          {
            user_id: profile.id,
            ideation_id: ideationId,
            study_type: t,
            score: scores[t],
            answers: answers[t],
          },
          { onConflict: 'ideation_id,study_type' },
        );
        if (err) throw err;
      }
      const viability = viabilityFromScore(sum);
      const conclusion = buildFeasibilityConclusionReport(scores);
      const { error: scoreErr } = await supabase.from('feasibility_scores').upsert(
        {
          user_id: profile.id,
          ideation_id: ideationId,
          score_technical: scores.technical,
          score_legal: scores.legal,
          score_financial: scores.financial,
          score_commercial: scores.commercial,
          score_organizational: scores.organizational,
          score_final: sum,
          viability,
          report_results: conclusion.results,
          report_strengths: conclusion.strengths.join('\n'),
          report_weaknesses: conclusion.weaknesses.join('\n'),
          report_recommendations: conclusion.recommendations.join('\n'),
        },
        { onConflict: 'ideation_id' },
      );
      if (scoreErr) throw scoreErr;
      setSavedScores({ ...scores });
      setReport(conclusion);
      toast.success(
        `Score final ${sum} — ${viability === 'viable' ? 'projet viable' : 'projet non viable'}. Rapport enregistré.`,
        'Faisabilité',
      );
      onDone?.(sum);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Calcul final impossible');
    } finally {
      setLoading(false);
    }
  }

  function downloadReport() {
    if (!report) return;
    openPrintablePdf(
      `Rapport d’étude de faisabilité — ${meta?.title || 'Projet'}`,
      buildConclusionReportPdfHtml({
        meta: {
          'Intitulé projet': meta?.title || '—',
          'Domaine d’activité': meta?.domain || '—',
          'Zone d’opération': meta?.zone || '—',
        },
        viability: report.viability,
        results: report.results,
        strengths: report.strengths,
        weaknesses: report.weaknesses,
        recommendations: report.recommendations,
      }),
    );
  }

  const questions = FEASIBILITY_QUESTIONS[active];
  const activeIdx = FEASIBILITY_TYPES.indexOf(active);
  const isFirst = activeIdx === 0;
  const isLast = activeIdx === FEASIBILITY_TYPES.length - 1;
  const validatedCount = FEASIBILITY_TYPES.filter((t) => savedScores[t] != null).length;
  const allStudiesValidated = validatedCount === FEASIBILITY_TYPES.length;

  function goNext() {
    if (!isLast) setActive(FEASIBILITY_TYPES[activeIdx + 1]!);
  }

  function goPrev() {
    if (!isFirst) setActive(FEASIBILITY_TYPES[activeIdx - 1]!);
  }

  const nextType = !isLast ? FEASIBILITY_TYPES[activeIdx + 1]! : null;

  return (
    <div>
      <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
        {FEASIBILITY_TYPES.map((t) => (
          <button
            key={t}
            type="button"
            className={`btn ${active === t ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => setActive(t)}
          >
            {FEASIBILITY_LABELS[t]}
            {savedScores[t] != null ? ` ✓` : ''}
          </button>
        ))}
      </div>

      <p className="muted" style={{ marginBottom: '0.35rem' }}>
        Étude {activeIdx + 1} sur {FEASIBILITY_TYPES.length}
      </p>
      <h4 style={{ marginTop: 0 }}>{FEASIBILITY_LABELS[active]} — notez chaque critère (0 à 5)</h4>
      {questions.map((q) => (
        <div className="field" key={q.id}>
          <label>
            {q.label} <span className="muted">(max {q.maxPoints} pts)</span>
          </label>
          <input
            type="range"
            min={0}
            max={5}
            step={1}
            value={answers[active]?.[q.id] ?? 0}
            onChange={(e) =>
              setAnswers({
                ...answers,
                [active]: { ...answers[active], [q.id]: Number(e.target.value) },
              })
            }
          />
          <span>{answers[active]?.[q.id] ?? 0}/5</span>
        </div>
      ))}
      <p>
        Sous-total {FEASIBILITY_LABELS[active]} : <strong>{scores[active]}</strong>
      </p>
      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <button type="button" className="btn btn-ghost" disabled={loading || isFirst} onClick={goPrev}>
          Précédent
        </button>
        <button
          type="button"
          className="btn btn-ghost"
          disabled={loading}
          onClick={() => void saveCurrentStudy()}
        >
          {savedScores[active] != null ? 'Mettre à jour cette étude' : 'Valider cette étude'}
        </button>
        {!isLast && nextType ? (
          <button type="button" className="btn btn-primary" disabled={loading} onClick={goNext}>
            Suivant → {FEASIBILITY_LABELS[nextType]}
          </button>
        ) : null}
      </div>
      <p className="muted" style={{ marginTop: '0.75rem' }}>
        {allStudiesValidated
          ? 'Les 5 études sont validées. Vous pouvez calculer le score final.'
          : `Progression : ${validatedCount}/5 études validées — validez chaque étude avant le calcul final.`}
      </p>
      <div style={{ marginTop: '0.75rem' }}>
        <button
          type="button"
          className="btn btn-primary"
          disabled={loading || !allStudiesValidated || !!report}
          onClick={() => void calculateFinal()}
        >
          Calculer le score final
        </button>
      </div>
      <p style={{ marginTop: '1rem' }}>
        Total actuel : <strong>{sum}</strong> / seuil {FEASIBILITY_VIABILITY_THRESHOLD} —{' '}
        <span className={`chip chip-normal ${sum >= FEASIBILITY_VIABILITY_THRESHOLD ? 'ok' : 'warn'}`}>
          {feasibilityViabilityLabel(viabilityFromScore(sum))}
        </span>
      </p>

      {report ? (
        <section style={{ marginTop: '1.5rem', borderTop: '1px solid var(--line)', paddingTop: '1rem' }}>
          <h4 style={{ marginTop: 0 }}>Conclusions</h4>
          <p>
            <strong>1. Résultats</strong>
            <br />
            {report.results}
          </p>
          <p>
            <strong>2. Points forts</strong>
          </p>
          <ul>
            {report.strengths.map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ul>
          <p>
            <strong>3. Points faibles</strong>
          </p>
          <ul>
            {report.weaknesses.map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ul>
          <p>
            <strong>4. Recommandations</strong>
          </p>
          <ul>
            {report.recommendations.map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ul>
          <button type="button" className="btn btn-primary" onClick={downloadReport}>
            Imprimer / Enregistrer en PDF
          </button>
        </section>
      ) : null}
    </div>
  );
}
